import { useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "../../hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daenoqptto0ceqqnh0o0";

type Status = "idle" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  useSeo({
    title: "联系我们｜投稿报料与反馈 - 今天吃瓜",
    description:
      "通过「今天吃瓜」联系表单提交吃瓜报料、内容反馈或合作咨询，也可直接发送邮件至 gossip@jintianchigua.com，我们会在核实后尽快处理。",
    keywords: "联系今天吃瓜,吃瓜报料,投稿,意见反馈",
    path: "/contact",
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系今天吃瓜",
        url: "https://emotechai.cn/contact",
        inLanguage: "zh-CN",
      },
    ],
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("idle");
    setFormError("");

    const form = e.currentTarget;
    const fd = new FormData(form);

    // honeypot anti-spam: silently treat as a normal submission
    const honey = (fd.get("company_alt") as string) || "";
    if (honey.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    if (!(fd.get("email") as string)?.trim()) {
      setStatus("error");
      setFormError("请填写可用的联系邮箱，方便我们回复你。");
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key === "company_alt") return;
      if (String(value).trim() !== "") payload.append(key, String(value));
    });

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let code = "";
      let serverMsg = "";
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code ?? "";
        serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      } catch {
        /* non-json */
      }
      const looksSpam = /spam/i.test(responseText) || /spam/i.test(serverMsg);
      if (res.ok && code === "OK" && !looksSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || looksSpam ? "提交被系统判定为异常，请稍后重试。" : "提交失败，请检查网络后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("提交失败，请检查网络后重试。");
    }
  };

  return (
    <div className="bg-background-50">
      <section className="relative overflow-hidden border-b border-background-200">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-500 via-accent-400 to-primary-500" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 py-14 text-center md:px-6">
          <h1 className="font-heading text-4xl font-black text-background-50 md:text-5xl">联系我们</h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-background-50/90">
            有新鲜瓜料想报料？对内容有疑问或建议？欢迎留言，我们会认真阅读每一份来稿，并在核实后尽快与你联系。
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-12 md:px-6 lg:grid-cols-[1fr_1.5fr]">
        {/* info */}
        <div className="flex flex-col gap-4">
          <div className="rounded-2xl border border-background-200 bg-background-100/60 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">其他联系方式</h2>
            <div className="mt-4 flex flex-col gap-4 text-sm">
              <a href="mailto:gossip@jintianchigua.com" className="group flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">官方邮箱</span>
                  <span className="font-medium group-hover:text-primary-600">gossip@jintianchigua.com</span>
                </span>
              </a>
              <div className="flex items-center gap-3 text-foreground-700">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">回复时效</span>
                  <span className="font-medium">工作日 1–3 个自然日内</span>
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-primary-50 p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">写清楚更容易被采纳</h2>
            <ul className="mt-4 flex flex-col gap-3 text-sm text-foreground-600">
              <li className="flex items-start gap-2"><i className="ri-check-double-line mt-0.5 text-primary-600" aria-hidden="true" />尽量写明事件主体、时间与大致经过</li>
              <li className="flex items-start gap-2"><i className="ri-check-double-line mt-0.5 text-primary-600" aria-hidden="true" />可附上公开可查的链接作为佐证</li>
              <li className="flex items-start gap-2"><i className="ri-check-double-line mt-0.5 text-primary-600" aria-hidden="true" />权益反馈请说明具体内容与你希望的处理方式</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-100/60 p-6 text-sm text-foreground-600">
            <p className="flex items-start gap-2">
              <i className="ri-information-line mt-0.5 text-accent-600" aria-hidden="true" />
              如内容涉及你的合法权益，反馈时请尽量提供身份说明，我们会依据可核实的信息第一时间响应处理。
            </p>
          </div>
        </div>

        {/* form */}
        <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-2xl font-black text-foreground-950">给我们留言</h2>
          <p className="mt-2 text-sm text-foreground-500">标 * 的为必填项，信息仅用于回复你的留言。</p>

          {status === "success" && (
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-secondary-200 bg-secondary-50 p-4 text-sm text-secondary-900">
              <i className="ri-checkbox-circle-line mt-0.5 text-lg" aria-hidden="true" />
              <p>感谢你的留言！我们已收到，会尽快查看并回复。新瓜见～</p>
            </div>
          )}
          {status === "error" && (
            <div className="mt-5 flex items-start gap-3 rounded-xl border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800">
              <i className="ri-error-warning-line mt-0.5 text-lg" aria-hidden="true" />
              <p>{formError || "提交失败，请稍后重试。"}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-5" data-readdy-form>
            {/* honeypot */}
            <div className="gu-field-trap" aria-hidden="true">
              <label htmlFor="company_alt">公司</label>
              <input
                type="text"
                id="company_alt"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-foreground-800">怎么称呼你 *</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  maxLength={30}
                  placeholder="你的称呼"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-foreground-800">联系邮箱 *</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div>
              <label htmlFor="contact-type" className="mb-1.5 block text-sm font-medium text-foreground-800">留言类型 *</label>
              <select
                id="contact-type"
                name="topic"
                required
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="报料投稿">吃瓜报料 / 投稿</option>
                <option value="内容反馈">内容意见反馈</option>
                <option value="权益反馈">权益相关反馈</option>
                <option value="合作咨询">合作咨询</option>
              </select>
            </div>

            <div>
              <label htmlFor="contact-msg" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容 *</label>
              <textarea
                id="contact-msg"
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="请尽量清晰地描述你的报料或反馈内容……"
                className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
              <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
            </div>

            <button
              type="submit"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              <i className="ri-send-plane-line" aria-hidden="true" />
              提交留言
            </button>
            <p className="text-center text-xs text-foreground-400">
              提交即表示你已阅读并同意我们仅将信息用于回复本次留言。
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}